/**
 * 合规开户Session标识统一管理工具
 * 统一管理所有合规相关的session标识
 */

// Session标识常量
export const COMPLIANCE_SESSION_KEYS = {
    // 合规开户标识（主标识）
    COMPLIANCE_AUDIT_FLAG: 'complianceAuditFlag',
    // 合规出入金标识（子标识）
    COMPLIANCE_IN_OUT_FLAG: 'complianceInOutFlag'
}

// 标识值常量
export const COMPLIANCE_VALUES = {
    YES: 'Y',
    TRUE: 'true',
    NO: 'N',
    FALSE: 'false'
}

/**
 * 检查值是否为有效的合规标识
 * @param {string} value - 要检查的值
 * @returns {boolean}
 */
function isValidComplianceValue(value) {
    return value === COMPLIANCE_VALUES.YES || value === COMPLIANCE_VALUES.TRUE
}

/**
 * 设置合规开户标识
 * @param {string} value - 标识值，默认为 'Y'
 */
export function setComplianceAuditFlag(value = COMPLIANCE_VALUES.YES) {
    sessionStorage.setItem(COMPLIANCE_SESSION_KEYS.COMPLIANCE_AUDIT_FLAG, value)
    console.log(`合规开户标识已设置: ${value}`)
}

/**
 * 获取合规开户标识
 * @returns {string|null}
 */
export function getComplianceAuditFlag() {
    return sessionStorage.getItem(COMPLIANCE_SESSION_KEYS.COMPLIANCE_AUDIT_FLAG)
}

/**
 * 检查是否为合规开户流程
 * @returns {boolean}
 */
export function isComplianceAuditFlow() {
    const flag = getComplianceAuditFlag()
    return isValidComplianceValue(flag)
}

/**
 * 设置合规出入金标识
 * @param {string} value - 标识值，默认为 'Y'
 */
export function setComplianceInOutFlag(value = COMPLIANCE_VALUES.YES) {
    sessionStorage.setItem(COMPLIANCE_SESSION_KEYS.COMPLIANCE_IN_OUT_FLAG, value)
    console.log(`合规出入金标识已设置: ${value}`)
}

/**
 * 获取合规出入金标识
 * @returns {string|null}
 */
export function getComplianceInOutFlag() {
    return sessionStorage.getItem(COMPLIANCE_SESSION_KEYS.COMPLIANCE_IN_OUT_FLAG)
}

/**
 * 检查是否为合规出入金流程
 * @returns {boolean}
 */
export function isComplianceInOutFlow() {
    const flag = getComplianceInOutFlag()
    return isValidComplianceValue(flag)
}

/**
 * 同时设置合规开户和合规出入金标识
 * @param {object} options - 配置选项
 * @param {string} options.auditFlag - 合规开户标识值
 * @param {string} options.inOutFlag - 合规出入金标识值
 */
export function setComplianceFlags({ auditFlag = COMPLIANCE_VALUES.YES, inOutFlag = COMPLIANCE_VALUES.YES } = {}) {
    setComplianceAuditFlag(auditFlag)
    setComplianceInOutFlag(inOutFlag)
}

/**
 * 获取所有合规标识
 * @returns {object}
 */
export function getAllComplianceFlags() {
    return {
        complianceAuditFlag: getComplianceAuditFlag(),
        complianceInOutFlag: getComplianceInOutFlag(),
        isComplianceAudit: isComplianceAuditFlow(),
        isComplianceInOut: isComplianceInOutFlow()
    }
}

/**
 * 清除合规开户标识
 */
export function clearComplianceAuditFlag() {
    sessionStorage.removeItem(COMPLIANCE_SESSION_KEYS.COMPLIANCE_AUDIT_FLAG)
    console.log('合规开户标识已清除')
}

/**
 * 清除合规出入金标识
 */
export function clearComplianceInOutFlag() {
    sessionStorage.removeItem(COMPLIANCE_SESSION_KEYS.COMPLIANCE_IN_OUT_FLAG)
    console.log('合规出入金标识已清除')
}

/**
 * 清除所有合规标识
 */
export function clearAllComplianceFlags() {
    clearComplianceAuditFlag()
    clearComplianceInOutFlag()
    console.log('所有合规标识已清除')
}

/**
 * 从URL参数中检测并设置合规标识
 * @param {object} options - 配置选项
 * @param {string} options.url - 要检测的URL
 * @param {object} options.route - Vue路由对象
 * @param {object} options.getParamet - login store中的getParamet
 */
export function detectAndSetComplianceFlags({ url, route, getParamet } = {}) {
    let complianceDetected = false
    let complianceInOutDetected = false
    
    // 1. 检查路由查询参数
    if (route?.query) {
        const { compliance, complianceInOut } = route.query
        if (isValidComplianceValue(compliance)) {
            setComplianceAuditFlag(compliance)
            complianceDetected = true
        }
        if (isValidComplianceValue(complianceInOut)) {
            setComplianceInOutFlag(complianceInOut)
            complianceInOutDetected = true
        }
    }
    
    // 2. 检查URL查询参数（包括hash后的查询参数）
    if (url || window.location.search || window.location.hash) {
        let searchParams = ''

        // 检查普通的查询参数
        if (url) {
            const urlObj = new URL(url, window.location.origin)
            searchParams = urlObj.search
        } else if (window.location.search) {
            searchParams = window.location.search
        }

        // 检查hash后的查询参数
        const hash = url ? new URL(url, window.location.origin).hash : window.location.hash
        if (hash && hash.includes('?')) {
            const hashQuery = hash.split('?')[1]
            if (hashQuery) {
                searchParams = searchParams ? `${searchParams}&${hashQuery}` : `?${hashQuery}`
            }
        }

        if (searchParams) {
            const urlParams = new URLSearchParams(searchParams)
            const compliance = urlParams.get('compliance')
            const complianceInOut = urlParams.get('complianceInOut')

            if (isValidComplianceValue(compliance)) {
                setComplianceAuditFlag(compliance)
                complianceDetected = true
            }
            if (isValidComplianceValue(complianceInOut)) {
                setComplianceInOutFlag(complianceInOut)
                complianceInOutDetected = true
            }
        }
    }
    
    // 3. 检查getParamet中的path参数
    if (getParamet?.path) {
        const compliance = extractQueryFromUrl('compliance', getParamet.path)
        const complianceInOut = extractQueryFromUrl('complianceInOut', getParamet.path)
        
        if (isValidComplianceValue(compliance)) {
            setComplianceAuditFlag(compliance)
            complianceDetected = true
        }
        if (isValidComplianceValue(complianceInOut)) {
            setComplianceInOutFlag(complianceInOut)
            complianceInOutDetected = true
        }
    }
    
    return {
        complianceDetected,
        complianceInOutDetected,
        flags: getAllComplianceFlags()
    }
}

/**
 * 从URL字符串中提取查询参数（支持hash后的查询参数）
 * @param {string} param - 参数名
 * @param {string} url - URL字符串
 * @returns {string|null}
 */
function extractQueryFromUrl(param, url) {
    try {
        const urlObj = new URL(url, window.location.origin)

        // 先检查普通的查询参数
        let value = urlObj.searchParams.get(param)
        if (value) return value

        // 再检查hash后的查询参数
        if (urlObj.hash && urlObj.hash.includes('?')) {
            const hashQuery = urlObj.hash.split('?')[1]
            if (hashQuery) {
                const hashParams = new URLSearchParams(hashQuery)
                value = hashParams.get(param)
                if (value) return value
            }
        }

        return null
    } catch (error) {
        // 如果URL解析失败，尝试正则匹配
        const regex = new RegExp(`[?&]${param}=([^&#]*)`)
        const match = url.match(regex)
        return match ? decodeURIComponent(match[1]) : null
    }
}

/**
 * 打印合规标识状态（用于调试）
 */
export function logComplianceStatus() {
    const flags = getAllComplianceFlags()
    console.log('=== 合规标识状态 ===')
    console.log('合规开户标识:', flags.complianceAuditFlag, '(有效:', flags.isComplianceAudit, ')')
    console.log('合规出入金标识:', flags.complianceInOutFlag, '(有效:', flags.isComplianceInOut, ')')
    console.log('==================')
    return flags
}

/**
 * 调试URL参数检测（用于测试）
 */
export function debugUrlDetection(testUrl = window.location.href) {
    console.log('=== URL参数检测调试 ===')
    console.log('测试URL:', testUrl)
    console.log('window.location.href:', window.location.href)
    console.log('window.location.search:', window.location.search)
    console.log('window.location.hash:', window.location.hash)

    // 测试各种检测方法
    const urlObj = new URL(testUrl, window.location.origin)
    console.log('URL对象解析:')
    console.log('  - search:', urlObj.search)
    console.log('  - hash:', urlObj.hash)

    // 检测普通查询参数
    const normalParams = new URLSearchParams(urlObj.search)
    console.log('普通查询参数:')
    console.log('  - compliance:', normalParams.get('compliance'))
    console.log('  - complianceInOut:', normalParams.get('complianceInOut'))

    // 检测hash后的查询参数
    if (urlObj.hash && urlObj.hash.includes('?')) {
        const hashQuery = urlObj.hash.split('?')[1]
        const hashParams = new URLSearchParams(hashQuery)
        console.log('Hash查询参数:')
        console.log('  - compliance:', hashParams.get('compliance'))
        console.log('  - complianceInOut:', hashParams.get('complianceInOut'))
    }

    // 使用extractQueryFromUrl测试
    console.log('extractQueryFromUrl测试:')
    console.log('  - compliance:', extractQueryFromUrl('compliance', testUrl))
    console.log('  - complianceInOut:', extractQueryFromUrl('complianceInOut', testUrl))

    console.log('========================')
}

/**
 * 检测并保存合规标识，返回合规状态
 * 这是一个简化的函数，用于在路由和组件中快速检测和保存合规标识
 * @returns {Object} 返回合规状态信息
 */
export function detectAndSaveComplianceFlags() {
    try {
        // 从 URL 参数中检测合规标识
        const urlParams = new URLSearchParams(window.location.search)
        const hashParams = new URLSearchParams(window.location.hash.split('?')[1] || '')

        const complianceFromUrl = urlParams.get('compliance') || hashParams.get('compliance')
        const complianceInOutFromUrl = urlParams.get('complianceInOut') || hashParams.get('complianceInOut')

        // 如果从 URL 检测到合规标识，立即保存到 session
        if (isValidComplianceValue(complianceFromUrl)) {
            setComplianceAuditFlag(complianceFromUrl)
            setComplianceInOutFlag(complianceFromUrl) // compliance=Y 时默认也设置合规出入金标识
            console.log('检测到合规开户标识，已保存到 session')
        }

        if (isValidComplianceValue(complianceInOutFromUrl)) {
            setComplianceInOutFlag(complianceInOutFromUrl)
            console.log('检测到合规出入金标识，已保存到 session')
        }

        // 返回最终的合规状态
        return {
            isCompliance: isComplianceAuditFlow(),
            isComplianceInOut: isComplianceInOutFlow()
        }
    } catch (error) {
        console.error('检测合规标识时发生错误:', error)

        // 发生错误时返回默认状态（基于当前 session 中的值）
        return {
            isCompliance: isComplianceAuditFlow(),
            isComplianceInOut: isComplianceInOutFlow()
        }
    }
}
